





























































































































































import { Component, Vue } from "vue-property-decorator";
import router from "@/router";
import rest from "@/rest";
import { vuex } from "@/store";
// components
import SimpleMessageBox from "@/components/Shared/SimpleMessageBox.vue";
import TrainingAppointmentsPanel from "@/components/Academy/TrainingAppointmentsPanel.vue";
import TrainingEventExBundlesPanel from "@/components/Academy/TrainingEventExBundlesPanel.vue";
// interfaces
import { ParamDictionary } from "@/interfaces/ParamDictionary";
import { TrainingEventDetailVm } from "@/interfaces/TrainingEventDetailVm";
import { TrainingEventStatus } from "@/interfaces/TrainingEventStatus";

@Component({
  components: {
    SimpleMessageBox,
    TrainingAppointmentsPanel,
    TrainingEventExBundlesPanel
  }
})
export default class TrainingEventEditView extends Vue {
  readonly TrainingEventStatus = TrainingEventStatus;

  tab = 0;

  detail: TrainingEventDetailVm | null = null;
  loading = false;
  saving = false;
  changingStatus = false;
  valid = true;
  showArchiveDialog = false;

  title = "";
  description = "";
  isPublic = false;
  registrationDeadlineDays: string | null = "";
  cancellationDeadlineDays: string | null = "";
  waitlistEnabled = true;

  readonly titleRules = [
    (v: string) => !!(v && v.trim()) || "Bitte einen Titel eingeben.",
    (v: string) => (v || "").length <= 200 || "Maximal 200 Zeichen."
  ];

  // Optional whole number between 0 and 365
  readonly daysRules = [
    (v: string | null) => v === null || v === "" || (/^\d+$/.test(String(v)) && Number(v) <= 365) || "Ganze Zahl von 0 bis 365."
  ];

  get eventId(): number {
    return Number(this.$route.params.id);
  }

  get breadcrumbs() {
    return [
      { text: "Start", to: "/", exact: true },
      { text: "Veranstaltungen", to: "/trainingEvents", exact: true },
      { text: this.detail?.title || vuex.globals.selectedEvent?.title || "Veranstaltung", disabled: true }
    ];
  }

  // Archived and cancelled events are read only
  get isEditable(): boolean {
    return !!this.detail && (this.detail.status === TrainingEventStatus.Draft || this.detail.status === TrainingEventStatus.Published);
  }

  get canChange(): boolean {
    return !!this.detail?.canEdit && this.isEditable;
  }

  get statusMeta(): { text: string; color: string } {
    switch (this.detail?.status) {
      case TrainingEventStatus.Published:
        return { text: "Veröffentlicht", color: "success" };
      case TrainingEventStatus.Cancelled:
        return { text: "Abgesagt", color: "error" };
      case TrainingEventStatus.Archived:
        return { text: "Archiviert", color: "grey" };
      default:
        return { text: "Entwurf", color: "grey lighten-1" };
    }
  }

  get lastChangedText(): string {
    if (!this.detail?.lastChanged) return "";
    const date = new Date(this.detail.lastChanged).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
    return `Zuletzt geändert: ${date}${this.detail.lastChangedBy ? " von " + this.detail.lastChangedBy : ""}`;
  }

  async mounted(): Promise<void> {
    await this.load();
  }

  eventParams(): ParamDictionary {
    const params: ParamDictionary = { dictionary: {} };
    params.dictionary!["EventId"] = this.eventId.toString();
    return params;
  }

  async load(): Promise<void> {
    this.loading = true;
    try {
      // On errors the rest wrapper shows the message and returns undefined
      const result: TrainingEventDetailVm | undefined = await rest.url("trainingLead/getEvent").post(this.eventParams());
      if (!result) {
        this.goBack();
        return;
      }
      this.applyDetail(result);
    } finally {
      this.loading = false;
    }
  }

  get orgName(): string {
    const org = vuex.globals.selectedOrg;
    return org && org.id === this.detail?.organizationId ? org.name || "" : "";
  }

  // Appointments and e-learnings changed: refresh the counts (and the status) without touching the form
  async reloadCounts(): Promise<void> {
    const result: TrainingEventDetailVm | undefined = await rest.url("trainingLead/getEvent").post(this.eventParams());
    if (result && this.detail) {
      this.detail = { ...this.detail, appointmentCount: result.appointmentCount, activeRegistrationCount: result.activeRegistrationCount, status: result.status };
    }
  }

  applyDetail(detail: TrainingEventDetailVm): void {
    this.detail = detail;
    this.title = detail.title || "";
    this.description = detail.description || "";
    this.isPublic = detail.isPublic;
    this.registrationDeadlineDays = detail.registrationDeadlineDays === null ? "" : String(detail.registrationDeadlineDays);
    this.cancellationDeadlineDays = detail.cancellationDeadlineDays === null ? "" : String(detail.cancellationDeadlineDays);
    this.waitlistEnabled = detail.waitlistEnabled;

    // Keep the selected event of the list in sync
    const selected = vuex.globals.selectedEvent;
    if (selected && selected.id === detail.id) {
      vuex.globals.setEvent({
        ...selected,
        title: detail.title,
        description: detail.description,
        status: detail.status,
        isPublic: detail.isPublic
      } as any);
    }
  }

  async save(): Promise<void> {
    if (!this.canChange || !(this.$refs.form as any).validate()) return;

    this.saving = true;
    try {
      // Empty values are sent as empty strings = not set
      const params = this.eventParams();
      params.dictionary!["Title"] = this.title.trim();
      params.dictionary!["Description"] = this.description.trim();
      params.dictionary!["IsPublic"] = this.isPublic.toString();
      params.dictionary!["RegistrationDeadlineDays"] = this.registrationDeadlineDays ?? "";
      params.dictionary!["CancellationDeadlineDays"] = this.cancellationDeadlineDays ?? "";
      params.dictionary!["WaitlistEnabled"] = this.waitlistEnabled.toString();

      const result: TrainingEventDetailVm | undefined = await rest.url("trainingLead/updateEvent").post(params);
      if (result) this.applyDetail(result);
    } finally {
      this.saving = false;
    }
  }

  async changeStatus(status: TrainingEventStatus): Promise<void> {
    this.changingStatus = true;
    try {
      const params = this.eventParams();
      params.dictionary!["Status"] = status.toString();
      const result: TrainingEventDetailVm | undefined = await rest.url("trainingLead/setEventStatus").post(params);
      if (result) this.applyDetail(result);
    } finally {
      this.changingStatus = false;
    }
  }

  goBack(): void {
    router.push({ name: "trainingEvents" });
  }
}
